class AnnouncementBar extends HTMLElement {
  constructor() {
    super()

    this.list = this.querySelector('[data-announcement-bar]')
    this.items = []
    this.prevBtn = null
    this.nextBtn = null
    this.currentIndex = 0
    this.autoplay = false
    this.showArrows = true
    this.slideDuration = 5000
    this.fadeDuration = 400
    this.autoplayTimer = null
    this.isPaused = false
    this.resizeFrame = null

    this.onPrev = this.onPrev.bind(this)
    this.onNext = this.onNext.bind(this)
    this.onMouseEnter = this.onMouseEnter.bind(this)
    this.onMouseLeave = this.onMouseLeave.bind(this)
    this.onFocusIn = this.onFocusIn.bind(this)
    this.onFocusOut = this.onFocusOut.bind(this)
    this.onVisibilityChange = this.onVisibilityChange.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onResize = this.onResize.bind(this)
  }

  connectedCallback() {
    if (!this.list) return

    const blockCount = parseInt(this.list.dataset.blockCount || '0', 10)

    // No rotation needed if there is only one (or zero) announcement.
    if (blockCount <= 1) return

    this.items = Array.from(this.list.querySelectorAll('.announcement-bar__item'))
    this.prevBtn = this.list.querySelector('[data-announcement-prev]')
    this.nextBtn = this.list.querySelector('[data-announcement-next]')

    this.autoplay = this.list.dataset.autoplay === 'true'
    this.showArrows = this.list.dataset.showArrows === 'true'
    this.slideDuration = Math.max(500, parseInt(this.list.dataset.slideDuration || '5000', 10))
    this.fadeDuration = Math.max(0, parseInt(this.list.dataset.fadeDuration || '400', 10))

    // Find the currently active item set by the server.
    const activeIndex = this.items.findIndex((item) => item.classList.contains('is-active'))
    this.currentIndex = activeIndex >= 0 ? activeIndex : 0

    this.bindEvents()
    // Wait one frame so the layout (font, width) is settled before measuring.
    requestAnimationFrame(() => this.positionArrows())
    this.startAutoplay()
  }

  disconnectedCallback() {
    this.unbindEvents()
    this.stopAutoplay()
  }

  bindEvents() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev)
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext)

    this.addEventListener('mouseenter', this.onMouseEnter)
    this.addEventListener('mouseleave', this.onMouseLeave)
    this.addEventListener('focusin', this.onFocusIn)
    this.addEventListener('focusout', this.onFocusOut)

    document.addEventListener('visibilitychange', this.onVisibilityChange)
    this.addEventListener('keydown', this.onKeyDown)

    window.addEventListener('resize', this.onResize)
  }

  unbindEvents() {
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev)
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext)

    this.removeEventListener('mouseenter', this.onMouseEnter)
    this.removeEventListener('mouseleave', this.onMouseLeave)
    this.removeEventListener('focusin', this.onFocusIn)
    this.removeEventListener('focusout', this.onFocusOut)

    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    this.removeEventListener('keydown', this.onKeyDown)

    window.removeEventListener('resize', this.onResize)
  }

  onPrev(event) {
    event.preventDefault()
    this.goTo(this.currentIndex - 1)
    this.restartAutoplay()
  }

  onNext(event) {
    event.preventDefault()
    this.goTo(this.currentIndex + 1)
    this.restartAutoplay()
  }

  onKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      this.goTo(this.currentIndex - 1)
      this.restartAutoplay()
    } else if (event.key === 'ArrowRight') {
      event.preventDefault()
      this.goTo(this.currentIndex + 1)
      this.restartAutoplay()
    }
  }

  onMouseEnter() {
    this.isPaused = true
    this.stopAutoplay()
  }

  onMouseLeave() {
    this.isPaused = false
    this.startAutoplay()
  }

  onFocusIn() {
    this.isPaused = true
    this.stopAutoplay()
  }

  onFocusOut(event) {
    // Only resume if focus has left the announcement bar entirely.
    if (this.contains(event.relatedTarget)) return
    this.isPaused = false
    this.startAutoplay()
  }

  onVisibilityChange() {
    if (document.hidden) {
      this.stopAutoplay()
    } else if (!this.isPaused) {
      this.startAutoplay()
    }
  }

  onResize() {
    // Debounce via requestAnimationFrame to avoid layout thrash on resize.
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame)
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = null
      this.positionArrows()
    })
  }

  positionArrows() {
    if (!this.list || !this.prevBtn || !this.nextBtn) return
    if (this.items.length === 0) return

    const activeItem = this.items[this.currentIndex]
    if (!activeItem) return

    const msg = activeItem.querySelector('.announcement-bar__message')
    if (!msg) return

    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
    const gap = 4 * rem
    const arrowWidth = this.prevBtn.offsetWidth || 24
    const listRect = this.list.getBoundingClientRect()

    // Measure the actual visible text by walking to the first text node.
    // range.selectNodeContents on the block .announcement-bar__message would
    // return the full list width (block elements fill their parent), so we
    // need the bounding box of the text characters themselves.
    const textNode = this.findFirstTextNode(msg)
    if (!textNode) return

    const range = document.createRange()
    range.selectNodeContents(textNode)
    const textRect = range.getBoundingClientRect()

    // Left arrow: right edge sits 4rem left of the text's left edge.
    const leftArrowLeft = textRect.left - listRect.left - gap - arrowWidth
    // Right arrow: left edge sits 4rem right of the text's right edge.
    const rightArrowRight = listRect.right - textRect.right - gap - arrowWidth

    this.prevBtn.style.left = `${leftArrowLeft}px`
    this.nextBtn.style.right = `${rightArrowRight}px`
  }

  findFirstTextNode(element) {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        // Skip empty whitespace-only text nodes between block elements.
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT
        return NodeFilter.FILTER_ACCEPT
      },
    })
    return walker.nextNode()
  }

  goTo(index) {
    if (this.items.length === 0) return

    const total = this.items.length
    const normalizedIndex = ((index % total) + total) % total

    if (normalizedIndex === this.currentIndex) return

    this.items[this.currentIndex].classList.remove('is-active')
    this.items[this.currentIndex].setAttribute('aria-hidden', 'true')
    this.items[this.currentIndex].inert = true

    this.items[normalizedIndex].classList.add('is-active')
    this.items[normalizedIndex].setAttribute('aria-hidden', 'false')
    this.items[normalizedIndex].inert = false

    this.currentIndex = normalizedIndex

    // Different slides have different text widths, so reposition the
    // arrows to keep the 4rem gap from the new active text.
    this.positionArrows()

    // Notify the sticky-header so it can recompute the announcement height.
    this.dispatchEvent(new CustomEvent('announcement-bar:change', {
      bubbles: true,
    }))
  }

  startAutoplay() {
    if (!this.autoplay || this.isPaused) return
    if (document.hidden) return
    if (this.autoplayTimer) return

    this.autoplayTimer = setInterval(() => {
      this.goTo(this.currentIndex + 1)
    }, this.slideDuration)
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = null
    }
  }

  restartAutoplay() {
    // A user-initiated action (click or keyboard) counts as engagement,
    // so clear the hover/focus pause and let the timer resume.
    this.isPaused = false
    this.stopAutoplay()
    this.startAutoplay()
  }
}

if (!customElements.get('announcement-bar')) {
  customElements.define('announcement-bar', AnnouncementBar)
}
